import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import backgroundImage from "./HeaderSlider_03.jpg";
import useScrollToTop from "./../../hooks/useScollToTop";
import { updateUserProfile } from "./../../services/userServices";
import {
  FaCamera,
  FaCheckCircle,
  FaEnvelope,
  FaIdBadge,
  FaPen,
  FaSave,
  FaTimes,
  FaUser,
  FaUserCircle,
} from "react-icons/fa";
import { getRoleFromRoleId } from "../../utils/roleHelper";
import { useUser } from "./../Home/UserContext";
import { useNotification } from "./../../components/Notification/NotificationManager";

const avatars = [
  "/img/avatar/avatarF001.webp",
  "/img/avatar/avatarF002.webp",
  "/img/avatar/avatarF003.webp",
  "/img/avatar/avatarF004.webp",
  "/img/avatar/avatarF005.webp",
  "/img/avatar/avatarF006.webp",
  "/img/avatar/avatarF007.webp",
  "/img/avatar/avatarF008.webp",
  "/img/avatar/avatarM001.webp",
  "/img/avatar/avatarM002.webp",
  "/img/avatar/avatarM003.webp",
  "/img/avatar/avatarM004.webp",
  "/img/avatar/avatarM005.webp",
  "/img/avatar/avatarM006.webp",
  "/img/avatar/avatarM007.webp",
  "/img/avatar/avatarM008.webp",
  "/img/avatar/avatarM009.webp",
];

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

const Profile = () => {
  useScrollToTop();
  const navigate = useNavigate();
  const { addNotification } = useNotification();
  const { user, updateUser } = useUser();

  const [isEditing, setIsEditing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [isSavingAvatar, setIsSavingAvatar] = useState(false);
  const [selectedAvatar, setSelectedAvatar] = useState("");
  const [localUser, setLocalUser] = useState({
    nom: "",
    prenom: "",
  });

  useEffect(() => {
    if (user) {
      setLocalUser({
        nom: user.nom || "",
        prenom: user.prenom || "",
      });
      setSelectedAvatar(user.photoURL || "");
    }
  }, [user]);

  const roleLabel = useMemo(() => {
    if (!user?.role_id) return "Non renseigné";
    return getRoleFromRoleId(user.role_id);
  }, [user]);

  const handleEdit = () => {
    if (!user) return;
    setLocalUser({
      nom: user.nom || "",
      prenom: user.prenom || "",
    });
    setIsEditing(true);
  };

  const handleCloseEditModal = () => {
    setIsEditing(false);
    if (user) {
      setLocalUser({
        nom: user.nom || "",
        prenom: user.prenom || "",
      });
    }
  };

  const handleCloseAvatarModal = () => {
    setIsUploading(false);
    setSelectedAvatar(user?.photoURL || "");
  };

  const normalizeAndStoreUser = (baseUser, updatedFields = {}) => {
    const normalizedUserData = {
      ...baseUser,
      ...updatedFields,
      userId:
        updatedFields.userId ||
        updatedFields.id ||
        baseUser?.userId ||
        baseUser?.id,
    };

    updateUser(normalizedUserData);
    localStorage.setItem("userInfo", JSON.stringify(normalizedUserData));
    return normalizedUserData;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const token = localStorage.getItem("token");
    if (!token || !user?.userId) {
      addNotification("Session expirée, veuillez vous reconnecter.", "error");
      return;
    }

    try {
      setIsSavingProfile(true);

      const updatedUser = await updateUserProfile(user.userId, localUser, token);

      const normalizedUser = normalizeAndStoreUser(user, {
        nom: updatedUser?.nom ?? localUser.nom,
        prenom: updatedUser?.prenom ?? localUser.prenom,
        email: updatedUser?.email ?? user.email,
        photoURL: updatedUser?.photoURL ?? user.photoURL,
        role_id: updatedUser?.role_id ?? user.role_id,
        id: updatedUser?.id ?? user.userId,
      });

      setLocalUser({
        nom: normalizedUser.nom || "",
        prenom: normalizedUser.prenom || "",
      });

      setIsEditing(false);
      addNotification("Profil mis à jour avec succès !", "success");
    } catch (error) {
      console.error("Erreur lors de la mise à jour du profil :", error);
      addNotification("Erreur lors de la mise à jour du profil.", "error");
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setLocalUser((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleUploadModal = () => {
    if (!user) return;
    setSelectedAvatar(user.photoURL || "");
    setIsUploading(true);
  };

  const handleAvatarSelection = async (avatarUrl) => {
    if (!user?.userId) return;

    try {
      setIsSavingAvatar(true);
      setSelectedAvatar(avatarUrl);

      const token = localStorage.getItem("token");
      if (!token) {
        addNotification("Session expirée, veuillez vous reconnecter.", "error");
        return;
      }

      await updateUserProfile(user.userId, { photoURL: avatarUrl }, token);

      normalizeAndStoreUser(user, {
        photoURL: avatarUrl,
      });

      addNotification("Photo de profil mise à jour avec succès !", "success");
      setIsUploading(false);
    } catch (error) {
      console.error(
        "Erreur lors de la mise à jour de la photo de profil :",
        error
      );
      addNotification(
        "Erreur lors de la mise à jour de la photo de profil.",
        "error"
      );
    } finally {
      setIsSavingAvatar(false);
    }
  };

  if (!user) {
    return (
      <div
        className="relative min-h-screen overflow-x-hidden bg-slate-950"
        style={{
          backgroundImage: `url(${backgroundImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-slate-950/70 backdrop-blur-[2px]" />

        <div className="relative z-10 flex min-h-screen items-center justify-center px-4 py-10">
          <div className="w-full max-w-xl rounded-[28px] border border-white/10 bg-white/95 p-8 text-center shadow-2xl dark:border-slate-700/60 dark:bg-slate-900/95">
            <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-300">
              <FaUserCircle className="text-4xl" />
            </div>

            <h1 className="mt-5 text-3xl font-black text-slate-900 dark:text-slate-100">
              Profil utilisateur
            </h1>
            <p className="mt-3 text-sm leading-6 text-slate-600 dark:text-slate-400">
              Vous devez être connecté pour consulter et modifier votre profil.
            </p>

            <div className="mt-6 flex flex-col justify-center gap-3 sm:flex-row">
              <button
                onClick={() => navigate("/login")}
                className="inline-flex items-center justify-center rounded-2xl bg-blue-600 px-5 py-3 font-semibold text-white transition hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600"
              >
                Se connecter
              </button>
              <button
                onClick={() => navigate("/register")}
                className="inline-flex items-center justify-center rounded-2xl border border-slate-300 bg-white px-5 py-3 font-semibold text-slate-800 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
              >
                Créer un compte
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="relative min-h-screen overflow-x-hidden bg-slate-950 font-satoshi"
      style={{
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <div className="absolute inset-0 bg-slate-950/70 backdrop-blur-[2px]" />

      <div className="relative z-10 px-4 pb-12 pt-[120px] sm:px-6">
        <div className="mx-auto max-w-6xl">
          {/* HERO */}
          <section className="overflow-hidden rounded-[30px] border border-white/10 bg-white/95 shadow-2xl dark:border-slate-700/60 dark:bg-slate-900/95">
            <div className="grid lg:grid-cols-[0.92fr_1.08fr]">
              {/* Colonne gauche */}
              <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900 p-6 text-white sm:p-8 lg:p-10">
                <p className="mb-3 inline-flex rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider">
                  Présence Club Pro
                </p>

                <h1 className="text-3xl font-black leading-tight sm:text-4xl">
                  Mon profil
                </h1>

                <p className="mt-4 max-w-lg text-sm leading-7 text-white/80">
                  Gérez vos informations personnelles, votre avatar et retrouvez
                  en un coup d’œil votre formule actuelle.
                </p>

                <div className="mt-8 flex flex-col items-center rounded-[24px] border border-white/10 bg-white/10 p-5 text-center backdrop-blur-sm">
                  {user?.photoURL ? (
                    <img
                      src={user.photoURL}
                      alt={`${user.prenom} ${user.nom}`}
                      className="h-32 w-32 rounded-full object-cover ring-4 ring-white/15"
                    />
                  ) : (
                    <div className="flex h-32 w-32 items-center justify-center rounded-full bg-white/10 ring-4 ring-white/15">
                      <FaUserCircle className="text-6xl text-white/80" />
                    </div>
                  )}

                  <h2 className="mt-4 text-2xl font-bold">
                    {user?.prenom} {user?.nom}
                  </h2>

                  <p className="mt-2 text-sm text-white/80">{user?.email}</p>

                  <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-emerald-400/10 px-4 py-2 text-sm font-semibold text-emerald-200">
                    <FaCheckCircle />
                    {roleLabel}
                  </div>

                  <button
                    onClick={handleUploadModal}
                    className="mt-5 inline-flex items-center gap-2 rounded-2xl bg-white px-4 py-3 font-semibold text-slate-900 transition hover:bg-white/90"
                  >
                    <FaCamera />
                    Modifier la photo
                  </button>
                </div>
              </div>

              {/* Colonne droite */}
              <div className="p-6 sm:p-8 lg:p-10">
                <div className="grid gap-5 md:grid-cols-2">
                  <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-800/60">
                    <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-50 text-blue-700 dark:bg-blue-500/10 dark:text-blue-300">
                      <FaUser />
                    </div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                      Nom
                    </p>
                    <p className="mt-2 text-lg font-bold text-slate-900 dark:text-slate-100">
                      {user?.nom || "Non renseigné"}
                    </p>
                  </div>

                  <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-800/60">
                    <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-50 text-blue-700 dark:bg-blue-500/10 dark:text-blue-300">
                      <FaUser />
                    </div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                      Prénom
                    </p>
                    <p className="mt-2 text-lg font-bold text-slate-900 dark:text-slate-100">
                      {user?.prenom || "Non renseigné"}
                    </p>
                  </div>

                  <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-800/60">
                    <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-50 text-blue-700 dark:bg-blue-500/10 dark:text-blue-300">
                      <FaEnvelope />
                    </div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                      Email
                    </p>
                    <p className="mt-2 break-all text-lg font-bold text-slate-900 dark:text-slate-100">
                      {user?.email || "Non renseigné"}
                    </p>
                  </div>

                  <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-800/60">
                    <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-50 text-blue-700 dark:bg-blue-500/10 dark:text-blue-300">
                      <FaIdBadge />
                    </div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                      Formule
                    </p>
                    <p className="mt-2 text-lg font-bold text-slate-900 dark:text-slate-100">
                      {roleLabel}
                    </p>
                  </div>
                </div>

                <div className="mt-6 rounded-[24px] border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                  <h3 className="text-xl font-black text-slate-900 dark:text-slate-100">
                    Gestion du profil
                  </h3>
                  <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-400">
                    Modifiez vos informations personnelles ou changez votre
                    avatar pour personnaliser votre compte.
                  </p>

                  <div className="mt-5 flex flex-col gap-3 sm:flex-row">
                    <button
                      onClick={handleEdit}
                      className="inline-flex items-center justify-center gap-2 rounded-2xl bg-blue-600 px-5 py-3 font-semibold text-white transition hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600"
                    >
                      <FaPen />
                      Modifier les informations
                    </button>

                    <button
                      onClick={handleUploadModal}
                      className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-300 bg-white px-5 py-3 font-semibold text-slate-800 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
                    >
                      <FaCamera />
                      Choisir un avatar
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      {/* MODALE AVATAR */}
      {isUploading && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm">
          <div className="max-h-[90vh] w-full max-w-4xl overflow-hidden rounded-[28px] border border-white/10 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-900">
            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4 dark:border-slate-800">
              <div>
                <h2 className="text-xl font-black text-slate-900 dark:text-slate-100">
                  Sélectionner un avatar
                </h2>
                <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                  Choisissez l’avatar qui représentera votre profil.
                </p>
              </div>

              <button
                onClick={handleCloseAvatarModal}
                className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 text-slate-600 transition hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
              >
                <FaTimes />
              </button>
            </div>

            <div className="max-h-[calc(90vh-88px)] overflow-y-auto p-5">
              <div className="grid grid-cols-3 gap-4 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6">
                {avatars.map((avatar, index) => {
                  const isSelected = selectedAvatar === avatar;

                  return (
                    <button
                      key={index}
                      type="button"
                      onClick={() => handleAvatarSelection(avatar)}
                      disabled={isSavingAvatar}
                      className={cn(
                        "group relative overflow-hidden rounded-2xl border-2 bg-slate-50 p-2 transition hover:scale-[1.02] dark:bg-slate-800",
                        isSelected
                          ? "border-blue-600 ring-4 ring-blue-100 dark:border-blue-400 dark:ring-blue-500/20"
                          : "border-slate-200 dark:border-slate-700"
                      )}
                    >
                      <img
                        src={avatar}
                        alt={`Avatar ${index + 1}`}
                        className="h-20 w-20 rounded-full object-cover sm:h-24 sm:w-24"
                      />

                      {isSelected && (
                        <div className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full bg-blue-600 text-xs text-white dark:bg-blue-500">
                          <FaCheckCircle />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>

              <div className="mt-6 flex justify-center">
                <button
                  onClick={handleCloseAvatarModal}
                  className="inline-flex items-center gap-2 rounded-2xl border border-slate-300 bg-white px-5 py-3 font-semibold text-slate-800 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
                >
                  <FaTimes />
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODALE EDIT */}
      {isEditing && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm">
          <div className="w-full max-w-xl overflow-hidden rounded-[28px] border border-white/10 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-900">
            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4 dark:border-slate-800">
              <div>
                <h2 className="text-xl font-black text-slate-900 dark:text-slate-100">
                  Modifier le profil
                </h2>
                <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                  Mettez à jour vos informations personnelles.
                </p>
              </div>

              <button
                onClick={handleCloseEditModal}
                className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 text-slate-600 transition hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
              >
                <FaTimes />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-5">
              <div className="space-y-4">
                <div>
                  <label
                    htmlFor="nom"
                    className="mb-2 block text-sm font-semibold text-slate-800 dark:text-slate-200"
                  >
                    Nom
                  </label>
                  <input
                    type="text"
                    id="nom"
                    name="nom"
                    value={localUser.nom}
                    onChange={handleChange}
                    className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:focus:border-blue-400 dark:focus:ring-blue-500/20"
                    placeholder="Votre nom"
                  />
                </div>

                <div>
                  <label
                    htmlFor="prenom"
                    className="mb-2 block text-sm font-semibold text-slate-800 dark:text-slate-200"
                  >
                    Prénom
                  </label>
                  <input
                    type="text"
                    id="prenom"
                    name="prenom"
                    value={localUser.prenom}
                    onChange={handleChange}
                    className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:focus:border-blue-400 dark:focus:ring-blue-500/20"
                    placeholder="Votre prénom"
                  />
                </div>
              </div>

              <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={handleCloseEditModal}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-300 bg-white px-5 py-3 font-semibold text-slate-800 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
                >
                  <FaTimes />
                  Annuler
                </button>

                <button
                  type="submit"
                  disabled={isSavingProfile}
                  className={cn(
                    "inline-flex items-center justify-center gap-2 rounded-2xl px-5 py-3 font-semibold text-white transition",
                    isSavingProfile
                      ? "cursor-not-allowed bg-slate-400"
                      : "bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600"
                  )}
                >
                  <FaSave />
                  {isSavingProfile ? "Sauvegarde..." : "Sauvegarder"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;